import { useCallback, useEffect, useState } from 'react';
import type { FleetSelection } from './FleetTierTable';

const STORAGE_PREFIX = 'korint.fleetSelection';

export const fleetSelectionDraftKey = ({
  policyId,
  branchId,
}: {
  policyId: string;
  branchId?: string;
}) => `${STORAGE_PREFIX}.${policyId}.${branchId ?? 'base'}`;

function readDraft(storageKey: string): FleetSelection {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? (JSON.parse(stored) as FleetSelection) : {};
  } catch {
    // localStorage unavailable (private mode, quota) or corrupted payload.
    return {};
  }
}

/**
 * Keeps the per-vehicle tier/peril selection across reloads and funnel back-and-forth: it is
 * only persisted server-side once the quote is submitted, so a large fleet would otherwise be
 * re-picked from scratch. `clearDraft` is the caller's job once the quote lands.
 */
export function useFleetSelectionDraft(storageKey: string) {
  const [selection, setSelection] = useState<FleetSelection>(() => readDraft(storageKey));

  useEffect(() => {
    if (Object.keys(selection).length === 0) {
      return;
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(selection));
    } catch {
      // Nothing to do: the draft is a convenience, the funnel still works without it.
    }
  }, [storageKey, selection]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Same as above.
    }
  }, [storageKey]);

  return [selection, setSelection, clearDraft] as const;
}
